import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Trash2, Save, Search, Package } from "lucide-react";
import { purchasesAPI, suppliersAPI, productsAPI, getApiErrorPayload } from "@/lib/api";
import { createPurchaseSchema } from "@/lib/schemas/purchase.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { Loading } from "@/components/ui/loader";
import { SectionHead } from "@/components/ui/section-head";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { FieldError, firstFieldErrors } from "@/components/ui/field-error";

interface PurchaseItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	barcode?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

interface PurchaseForm {
	supplierId: string;
	purchaseDate: string;
	status: "pending" | "confirmed" | "received" | "cancelled" | "refunded";
	paymentStatus: "unpaid" | "partial" | "paid";
	shippingCost: number;
	notes: string;
	items: PurchaseItem[];
}

const AddPurchasePage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { toast } = useToast();
	const currency = useCurrency();
	const isEditMode = !!id;
	const [loading, setLoading] = useState(isEditMode);
	const [saving, setSaving] = useState(false);
	const [suppliers, setSuppliers] = useState<any[]>([]);
	const [products, setProducts] = useState<any[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<PurchaseItem[]>([]);
	const [purchaseForm, setPurchaseForm] = useState<PurchaseForm>({
		supplierId: "",
		purchaseDate: new Date().toISOString().split("T")[0],
		status: "pending",
		paymentStatus: "unpaid",
		shippingCost: 0,
		notes: "",
		items: [],
	});
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	const { isDirty } = useIsDirty({ purchaseForm, selectedProducts }, !loading);
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	const clearFieldError = (field: string) => {
		setFieldErrors(prev => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	useEffect(() => {
		fetchSuppliers();
		fetchProducts();
	}, []);

	useEffect(() => {
		if (id) {
			fetchPurchaseForEdit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const fetchPurchaseForEdit = async () => {
		if (!id) return;
		try {
			setLoading(true);
			const data = await purchasesAPI.getById(id);
			const supplierId = String(data.supplierId || data.supplier?.id || "");
			const purchaseDate = data.purchaseDate
				? new Date(data.purchaseDate).toISOString().split("T")[0]
				: new Date().toISOString().split("T")[0];
			// Normalize for form: backend "pending" = form "unpaid"; backend "confirmed"/"cancelled" supported in dropdown
			const paymentStatus = data.paymentStatus === "pending" ? "unpaid" : data.paymentStatus || "unpaid";
			setPurchaseForm({
				supplierId,
				purchaseDate,
				status: data.status || "pending",
				paymentStatus: paymentStatus === "overdue" ? "unpaid" : paymentStatus,
				shippingCost: Number(data.shippingAmount ?? data.shippingCost) || 0,
				notes: data.notes || "",
				items: [],
			});
			const items: PurchaseItem[] = (data.items || []).map((item: any) => ({
				id: item.id || String(Date.now() + Math.random()),
				productId: item.productId,
				productName: item.product?.name ?? item.productName ?? "",
				productImage: item.product?.image ?? item.productImage,
				barcode: item.product?.barcode ?? item.barcode,
				quantity: Number(item.quantity) || 0,
				unitPrice: Number(item.unitPrice) || 0,
				discountPercentage: Number(item.discountRate ?? item.discountPercentage) || 0,
				discountAmount: Number(item.discountAmount) || 0,
				taxPercentage: Number(item.taxRate ?? item.taxPercentage) || 0,
				taxAmount: Number(item.taxAmount) || 0,
				totalAmount: Number(item.totalAmount) || 0,
			}));
			setSelectedProducts(items);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("purchases.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/purchases");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredProducts(products);
		} else {
			const filtered = products.filter(
				product =>
					product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					product.barcode?.toLowerCase().includes(searchQuery.toLowerCase()),
			);
			setFilteredProducts(filtered);
		}
	}, [searchQuery, products]);

	const fetchSuppliers = async () => {
		try {
			const data = await suppliersAPI.getAll({ page: 1, limit: 1000 });
			setSuppliers(data.suppliers || []);
		} catch (error) {
			console.error("Failed to fetch suppliers:", error);
		}
	};

	const fetchProducts = async () => {
		try {
			const data = await productsAPI.getAll({ page: 1, limit: 1000 });
			setProducts(data.products || []);
			setFilteredProducts(data.products || []);
		} catch (error) {
			console.error("Failed to fetch products:", error);
		}
	};

	const handleInputChange = (field: keyof PurchaseForm, value: any) => {
		setPurchaseForm(prev => ({ ...prev, [field]: value }));
		clearFieldError(field);
	};

	const addProductToPurchase = (product: any) => {
		clearFieldError("items");
		const existingItem = selectedProducts.find(item => item.productId === product.id);

		if (existingItem) {
			setSelectedProducts(prev =>
				prev.map(item => (item.productId === product.id ? { ...item, quantity: item.quantity + 1 } : item)),
			);
		} else {
			const newItem: PurchaseItem = {
				id: Date.now().toString(),
				productId: product.id,
				productName: product.name,
				productImage: product.image,
				barcode: product.barcode,
				quantity: 1,
				unitPrice: product.purchasePrice || 0,
				discountPercentage: 0,
				discountAmount: 0,
				taxPercentage: product.taxPercentage || 0,
				taxAmount: 0,
				totalAmount: product.purchasePrice || 0,
			};
			setSelectedProducts(prev => [...prev, newItem]);
		}

		setSearchQuery("");
		setFilteredProducts(products);
	};

	const updateItem = (itemId: string, field: keyof PurchaseItem, value: any) => {
		setSelectedProducts(prev =>
			prev.map(item => {
				if (item.id === itemId) {
					const updatedItem = { ...item, [field]: value };

					// Recalculate amounts
					const discountAmount =
						(updatedItem.unitPrice * updatedItem.quantity * updatedItem.discountPercentage) / 100;
					const taxAmount =
						((updatedItem.unitPrice * updatedItem.quantity - discountAmount) * updatedItem.taxPercentage) /
						100;
					const totalAmount = updatedItem.unitPrice * updatedItem.quantity - discountAmount + taxAmount;

					return {
						...updatedItem,
						discountAmount,
						taxAmount,
						totalAmount,
					};
				}
				return item;
			}),
		);
	};

	const removeItem = (itemId: string) => {
		setSelectedProducts(prev => prev.filter(item => item.id !== itemId));
	};

	const calculateTotals = () => {
		const subtotal = selectedProducts.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
		const totalDiscount = selectedProducts.reduce((sum, item) => sum + item.discountAmount, 0);
		const totalTax = selectedProducts.reduce((sum, item) => sum + item.taxAmount, 0);
		const total = subtotal - totalDiscount + totalTax + purchaseForm.shippingCost;

		return { subtotal, totalDiscount, totalTax, total };
	};

	const handleSubmit = async () => {
		const errors: Record<string, string> = {};
		if (!purchaseForm.supplierId) errors.supplierId = "purchases.errors.supplierRequired";
		if (selectedProducts.length === 0) errors.items = "purchases.errors.productsRequired";
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			toast({
				title: t("common.error"),
				description: t("validation.checkFields"),
				variant: "destructive",
			});
			return;
		}
		setFieldErrors({});

		try {
			setSaving(true);
			const { subtotal: sub, totalDiscount: totDisc, totalTax: totTax, total } = calculateTotals();
			const itemsForApi = selectedProducts.map(item => ({
				productId: Number(item.productId),
				quantity: item.quantity,
				unitPrice: Number(item.unitPrice),
				discountRate: Number(item.discountPercentage),
				taxRate: Number(item.taxPercentage),
				taxAmount: Number(item.taxAmount),
				totalAmount: Number(item.totalAmount),
				discountAmount: Number(item.discountAmount),
			}));

			// Map form paymentStatus to API: "unpaid" -> "pending"
			const paymentStatusForApi =
				purchaseForm.paymentStatus === "unpaid" ? "pending" : purchaseForm.paymentStatus;
			const purchaseData = {
				...purchaseForm,
				supplierId: purchaseForm.supplierId ? Number(purchaseForm.supplierId) : undefined,
				paymentStatus: paymentStatusForApi,
				shippingAmount: purchaseForm.shippingCost,
				items: itemsForApi,
				subtotal: sub,
				discountAmount: totDisc,
				taxAmount: totTax,
				totalAmount: total,
			};

			const parsed = createPurchaseSchema.safeParse(purchaseData);
			if (!parsed.success) {
				setFieldErrors(firstFieldErrors(parsed.error.issues));
				toast({
					title: t("common.error"),
					description: t("validation.checkFields"),
					variant: "destructive",
				});
				return;
			}

			if (isEditMode && id) {
				// Status is changed only from the purchase page, so receiving adds stock once.
				const { status: _status, ...updateData } = parsed.data;
				await purchasesAPI.update(id, updateData);
				toast({
					title: t("common.success"),
					description: t("purchases.messages.updated"),
				});
				allowNavigation();
				navigate("/purchases");
			} else {
				// "Received" is applied through the status endpoint after creating, which adds the stock.
				const receiveOnCreate = parsed.data.status === "received";
				const created = await purchasesAPI.create(
					receiveOnCreate ? { ...parsed.data, status: "pending" } : parsed.data,
				);
				if (receiveOnCreate && created?.id) {
					try {
						await purchasesAPI.updateStatus(created.id, "received");
					} catch {
						toast({
							title: t("common.error"),
							description: t("purchases.errors.savedNotReceived"),
							variant: "destructive",
						});
						allowNavigation();
						navigate(`/purchases/${created.id}`);
						return;
					}
				}
				toast({
					title: t("common.success"),
					description: t("purchases.messages.created"),
				});
				allowNavigation();
				navigate("/purchases");
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const { subtotal, totalDiscount, totalTax, total } = calculateTotals();

	if (loading && isEditMode) {
		return <Loading />;
	}

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">
							{isEditMode ? t("purchases.edit.title") : t("purchases.add.title")}
						</PageHeading>
						<p className="text-muted-foreground">
							{isEditMode ? t("purchases.edit.description") : t("purchases.add.description")}
						</p>
					</div>
				</div>
				<Button onClick={handleSubmit} disabled={saving}>
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{isEditMode ? t("purchases.edit.save") : t("purchases.add.save")}
				</Button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Purchase Form */}
				<div className="lg:col-span-2 space-y-6">
					{/* Operation Data */}
					<SectionHead num={1} title={t("purchases.add.operationData")} />
					<Card>
						<CardContent className="space-y-6 pt-6">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>
										{t("purchases.add.supplier")} <span className="text-destructive">*</span>
									</Label>
									<Select
										value={purchaseForm.supplierId}
										onValueChange={value => handleInputChange("supplierId", value)}
									>
										<SelectTrigger
											aria-label={t("purchases.add.supplier")}
											aria-invalid={!!fieldErrors.supplierId}
										>
											<SelectValue placeholder={t("purchases.add.selectSupplier")} />
										</SelectTrigger>
										<SelectContent>
											{suppliers.map(supplier => (
												<SelectItem key={supplier.id} value={String(supplier.id)}>
													{supplier.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldError message={fieldErrors.supplierId} />
								</div>
								<div className="space-y-2">
									<Label>{t("purchases.add.purchaseDate")}</Label>
									<Input
										aria-label={t("purchases.add.purchaseDate")}
										type="date"
										value={purchaseForm.purchaseDate}
										onChange={e => handleInputChange("purchaseDate", e.target.value)}
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								{!isEditMode && (
									<div className="space-y-2">
										<Label>{t("purchases.add.status")}</Label>
										<Select
											value={purchaseForm.status}
											onValueChange={value => handleInputChange("status", value)}
										>
											<SelectTrigger aria-label={t("purchases.add.status")}>
												<SelectValue />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="pending">{t("purchases.status.pending")}</SelectItem>
												<SelectItem value="confirmed">
													{t("purchases.status.confirmed")}
												</SelectItem>
												<SelectItem value="received">
													{t("purchases.status.received")}
												</SelectItem>
											</SelectContent>
										</Select>
										<p className="text-xs text-muted-foreground">
											{t("purchases.form.statusHint")}
										</p>
									</div>
								)}
								<div className="space-y-2">
									<Label>{t("purchases.add.paymentStatus")}</Label>
									<Select
										value={purchaseForm.paymentStatus}
										onValueChange={value => handleInputChange("paymentStatus", value)}
									>
										<SelectTrigger aria-label={t("purchases.add.paymentStatus")}>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="unpaid">
												{t("purchases.paymentStatus.unpaid")}
											</SelectItem>
											<SelectItem value="partial">
												{t("purchases.paymentStatus.partial")}
											</SelectItem>
											<SelectItem value="paid">{t("purchases.paymentStatus.paid")}</SelectItem>
										</SelectContent>
									</Select>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>{t("purchases.add.shipping")}</Label>
									<Input
										aria-label={t("purchases.add.shipping")}
										type="number"
										min="0"
										step="0.01"
										value={purchaseForm.shippingCost}
										onChange={e =>
											handleInputChange("shippingCost", parseFloat(e.target.value) || 0)
										}
										placeholder="0.00"
									/>
								</div>
							</div>

							<div className="space-y-2">
								<Label>{t("purchases.add.notes")}</Label>
								<Input
									aria-label={t("purchases.add.notes")}
									type="text"
									value={purchaseForm.notes}
									onChange={e => handleInputChange("notes", e.target.value)}
									placeholder={t("purchases.add.notesPlaceholder")}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Products Selection */}
					<SectionHead
						num={2}
						title={t("purchases.add.products")}
						hint={t("purchases.add.productsDescription")}
					/>
					<Card>
						<CardContent className="space-y-6 pt-6">
							{/* Product Search */}
							<div className="space-y-2">
								<Label>{t("purchases.add.searchProducts")}</Label>
								<div className="relative">
									<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
									<Input
										aria-label={t("purchases.add.searchProducts")}
										placeholder={t("purchases.add.searchPlaceholder")}
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
										className="ps-10"
									/>
								</div>
							</div>

							{/* Product list: all when search empty, filtered when typing */}
							<div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{filteredProducts.map(product => (
										<div
											key={product.id}
											className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
											onClick={() => addProductToPurchase(product)}
										>
											<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
												{product.image ? (
													<img
														src={product.image}
														alt={product.name}
														className="w-full h-full object-cover rounded-lg"
													/>
												) : (
													<Package className="h-6 w-6 text-primary" />
												)}
											</div>
											<div className="flex-1">
												<h4 className="font-medium">{product.name}</h4>
												<p className="text-sm text-muted-foreground">
													{product.barcode &&
														`${t("products.table.barcode")}: ${product.barcode}`}
												</p>
												<p className="text-sm font-medium">
													{t("purchases.add.price")}:{" "}
													<Price value={product.purchasePrice || 0} currency={currency} />
												</p>
											</div>
											<Button
												title={t("common.add")}
												aria-label={t("common.add")}
												size="sm"
												variant="outline"
											>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</div>

							{/* Selected Products */}
							<div className="space-y-4">
								<h4 className="font-medium">
									{t("purchases.add.selectedProducts")} <span className="text-destructive">*</span>
								</h4>
								<FieldError message={fieldErrors.items} />
								{selectedProducts.map(item => (
									<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
										<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
											{item.productImage ? (
												<img
													src={item.productImage}
													alt={item.productName}
													className="w-full h-full object-cover rounded-lg"
												/>
											) : (
												<Package className="h-8 w-8 text-primary" />
											)}
										</div>
										<div className="flex-1">
											<h4 className="font-medium">{item.productName}</h4>
											<div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-2">
												<div>
													<Label className="text-sm">{t("purchases.add.quantity")}</Label>
													<Input
														aria-label={t("purchases.add.quantity")}
														type="number"
														min="1"
														value={item.quantity}
														onChange={e =>
															updateItem(
																item.id,
																"quantity",
																parseInt(e.target.value) || 1,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("purchases.add.unitPrice")}</Label>
													<Input
														aria-label={t("purchases.add.unitPrice")}
														type="number"
														min="0"
														step="0.01"
														value={item.unitPrice}
														onChange={e =>
															updateItem(
																item.id,
																"unitPrice",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-24"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("purchases.add.discount")} </Label>
													<Input
														aria-label={t("purchases.add.discount")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.discountPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"discountPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("purchases.add.taxPercent")} </Label>
													<Input
														aria-label={t("purchases.add.taxPercent")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.taxPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"taxPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("purchases.add.total")}</Label>
													<div className="text-lg font-semibold">
														<Price value={item.totalAmount} currency={currency} />
													</div>
												</div>
											</div>
										</div>
										<Button
											title={t("common.delete")}
											aria-label={t("common.delete")}
											size="sm"
											variant="destructive"
											onClick={() => removeItem(item.id)}
										>
											<Trash2 className="h-4 w-4 text-white" />
										</Button>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Summary */}
				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("purchases.add.summary")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex justify-between">
								<span>{t("purchases.add.subtotal")}</span>
								<span className="font-semibold">
									<Price value={subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("purchases.add.discount")}</span>
								<span className="font-semibold text-destructive">
									{totalDiscount > 0 && "-"}
									<Price value={totalDiscount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("purchases.add.tax")}</span>
								<span className="font-semibold">
									<Price value={totalTax} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("purchases.add.shipping")}</span>
								<span className="font-semibold">
									<Price value={purchaseForm.shippingCost} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("purchases.add.total")}</span>
									<span>
										<Price value={total} currency={currency} />
									</span>
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Sticky action bar */}
			<div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-t-lg border-t bg-background/95 px-3 py-3 backdrop-blur">
				<Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={saving}>
					{t("common.cancel")}
				</Button>
				<Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-40">
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("common.save")}
				</Button>
			</div>
		</div>
	);
};

export default AddPurchasePage;
